import { useEffect, useState } from "react";
import { FaBell } from "react-icons/fa";
import Button from "react-bootstrap/Button";
import Badge from "react-bootstrap/Badge";
import Modal from "react-bootstrap/Modal";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Tooltip from "react-bootstrap/Tooltip";
import ListenerNotifications from "./ListenerComponents/ListenerNotifications";
import httpService from "../services/httpService";
import { useAuthStore } from "../store/store";
import { getEcho } from "../services/echoService";
import eventBus from "../services/eventBus";
import { getNotificationBody } from "../services/notificationRegistry";


const NotificationsButtonWithModal = ({
    tooltip_position = "top",
    type = "button",
    className = "",
    variant = "outline-primary"
}) => {
    const [showModal, setShowModal] = useState(false);
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [loading, setLoading] = useState(false);
    const { user } = useAuthStore();

    const fetchNotifications = () => {
        setLoading(true);
        httpService.get('/dash/notifications', { params: { per_page: 20 } })
            .then((res) => {
                setNotifications(res.data.data?.data ?? []);
                setUnreadCount(res.data.unread_count ?? 0);
            })
            .catch(() => {})
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        fetchNotifications();

        const userGuid = user?.guid;
        const echo = getEcho();
        console.log('[Echo] user guid:', userGuid, '| echo ready:', !!echo);
        if (!userGuid || !echo) {
            console.warn('[Echo] Skipping channel subscription — missing guid or echo instance');
            return;
        }

        const channelName = `user.${userGuid}`;
        console.log('[Echo] Subscribing to private channel:', channelName);
        const channel = echo.private(channelName);

        channel.notification((notification) => {
            console.log('[Echo] Notification received:', notification);
            eventBus.emit('toast', { type: notification.badge ?? 'info', message: notification.message ?? (getNotificationBody(notification) || 'You have a new notification') });
            const newNotification = {
                id: notification.id ?? `rt-${Date.now()}`,
                read_at: null,
                created_at: new Date().toISOString(),
                data: notification,
            };
            setNotifications(prev => [newNotification, ...prev]);
            setUnreadCount(prev => prev + 1);
        });

        return () => {
            echo.leave(`user.${userGuid}`);
        };
    }, []);

    const handleMarkRead = (id) => {
        httpService.patch(`/dash/notifications/${id}/read`)
            .then(() => {
                setNotifications(prev =>
                    prev.map(n => n.id === id ? { ...n, read_at: new Date().toISOString() } : n)
                );
                setUnreadCount(prev => Math.max(0, prev - 1));
            })
            .catch(() => {});
    };

    const handleMarkAllRead = () => {
        httpService.patch('/dash/notifications/read-all')
            .then(() => {
                setNotifications(prev =>
                    prev.map(n => ({ ...n, read_at: n.read_at ?? new Date().toISOString() }))
                );
                setUnreadCount(0);
            })
            .catch(() => {});
    };

    const handleDelete = (id) => {
        httpService.delete(`/dash/notifications/${id}`)
            .then(() => {
                setNotifications(prev => {
                    const deleted = prev.find(n => n.id === id);
                    if (deleted && !deleted.read_at) {
                        setUnreadCount(c => Math.max(0, c - 1));
                    }
                    return prev.filter(n => n.id !== id);
                });
            })
            .catch(() => {});
    };

    const handleShowModal = () => {
        fetchNotifications();
        setShowModal(true);
    };
    const handleCloseModal = () => setShowModal(false);

    return (
        <>
            <OverlayTrigger
                placement={tooltip_position}
                overlay={<Tooltip id="notifications-tooltip">Notifications</Tooltip>}
            >
                <Button
                    className={`${className || "btn-sm"} position-relative`}
                    variant={variant}
                    onClick={handleShowModal}
                    type={type}
                >
                    <FaBell />
                    {unreadCount > 0 && (
                        <Badge
                            bg="danger"
                            pill
                            className="position-absolute top-0 start-0 translate-middle"
                            style={{ fontSize: '0.6rem' }}
                        >
                            {unreadCount > 99 ? '99+' : unreadCount}
                        </Badge>
                    )}
                </Button>
            </OverlayTrigger>

            <Modal show={showModal} onHide={handleCloseModal} centered size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>
                        <FaBell className="me-2" />
                        Notifications
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                    <ListenerNotifications
                        notifications={notifications}
                        loading={loading}
                        onMarkRead={handleMarkRead}
                        onMarkAllRead={handleMarkAllRead}
                        onDelete={handleDelete}
                    />
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleCloseModal}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default NotificationsButtonWithModal;
